import { BehaviorSubject, filter, from, map, switchMap } from "rxjs";
import { getDb, type RecordDoc } from "@/lib/db";
import { profile$ } from "@/lib/profile";
import type { NotificationPreferencesData, UserRole } from "@/types/domain";

const NOTIFICATION_PREFERENCES_TYPE = "notification_preferences";

interface NotificationPreferencesState {
  loading: boolean;
  recordId: string | null;
  data: NotificationPreferencesData | null;
}

const state$ = new BehaviorSubject<NotificationPreferencesState>({
  loading: true,
  recordId: null,
  data: null,
});

profile$
  .pipe(
    filter((s) => !s.loading && s.profile !== null && s.user !== null),
    switchMap((s) =>
      from(getDb()).pipe(
        switchMap(
          (db) =>
            db.week_allowance.findOne({
              selector: {
                user_id: s.user!.id,
                type: NOTIFICATION_PREFERENCES_TYPE,
              },
            }).$,
        ),
      ),
    ),
    map((doc) => {
      if (!doc) return { loading: false, recordId: null, data: null };
      const record = doc.toJSON() as RecordDoc;
      return {
        loading: false,
        recordId: record.id,
        data: record.data as unknown as NotificationPreferencesData,
      };
    }),
  )
  .subscribe((next) => state$.next(next));

function defaultsForRole(
  role: UserRole,
  timezone: string,
): NotificationPreferencesData {
  if (role === "child") {
    return {
      no_log_today: { enabled: true, time: "20:00", timezone },
      close_week: {
        enabled: true,
        days_after_close: 0,
        time: "19:00",
        timezone,
      },
    };
  }
  return {
    kid_confirmed: { enabled: true },
    approval_pending: { enabled: true },
  };
}

function resolveTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Oslo";
  } catch {
    return "Europe/Oslo";
  }
}

export const notificationsService = {
  state$: state$.asObservable(),

  get snapshot(): NotificationPreferencesState {
    return state$.getValue();
  },

  async ensureDefaults(
    userId: string,
    familyId: string,
    role: UserRole,
  ): Promise<void> {
    if (state$.getValue().data !== null) return;
    const db = await getDb();
    const existing = await db.week_allowance
      .findOne({
        selector: { user_id: userId, type: NOTIFICATION_PREFERENCES_TYPE },
      })
      .exec();
    if (existing) return;
    const now = new Date().toISOString();
    const data = defaultsForRole(role, resolveTimezone());
    await db.week_allowance.insert({
      id: crypto.randomUUID(),
      type: NOTIFICATION_PREFERENCES_TYPE,
      family_id: familyId,
      user_id: userId,
      created_at: now,
      updated_at: now,
      data: data as unknown as Record<string, unknown>,
    });
  },

  async updatePreferences(
    patch: Partial<NotificationPreferencesData>,
  ): Promise<void> {
    const current = state$.getValue();
    if (!current.recordId) return;
    const db = await getDb();
    const doc = await db.week_allowance.findOne(current.recordId).exec();
    if (!doc) return;
    const json = doc.toJSON() as RecordDoc;
    const merged: NotificationPreferencesData = {
      ...(json.data as unknown as NotificationPreferencesData),
      ...patch,
    };
    await doc.patch({
      data: merged as unknown as Record<string, unknown>,
      updated_at: new Date().toISOString(),
    });
  },
};
